import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import Sheet from "components/sheet/Sheet";
import Input from "components/form/input/Input";
import { displayCurrency } from "helpers/Helpers";
import Portal from "elements/Portal";

export type TCashDenomination = {
  key: string;
  label: string;
  value: number;
  type: "bill" | "coin";
};

const CAD_DENOMINATIONS: TCashDenomination[] = [
  { key: "100", label: "$100", value: 100, type: "bill" },
  { key: "50", label: "$50", value: 50, type: "bill" },
  { key: "20", label: "$20", value: 20, type: "bill" },
  { key: "10", label: "$10", value: 10, type: "bill" },
  { key: "5", label: "$5", value: 5, type: "bill" },
  { key: "2", label: "$2", value: 2, type: "coin" },
  { key: "1", label: "$1", value: 1, type: "coin" },
  { key: "0.25", label: "25¢", value: 0.25, type: "coin" },
  { key: "0.10", label: "10¢", value: 0.1, type: "coin" },
  { key: "0.05", label: "5¢", value: 0.05, type: "coin" },
  { key: "0.01", label: "1¢", value: 0.01, type: "coin" },
];

const USD_DENOMINATIONS: TCashDenomination[] = [
  { key: "100", label: "$100", value: 100, type: "bill" },
  { key: "50", label: "$50", value: 50, type: "bill" },
  { key: "20", label: "$20", value: 20, type: "bill" },
  { key: "10", label: "$10", value: 10, type: "bill" },
  { key: "5", label: "$5", value: 5, type: "bill" },
  { key: "1", label: "$1", value: 1, type: "bill" },
  { key: "0.25", label: "25¢", value: 0.25, type: "coin" },
  { key: "0.10", label: "10¢", value: 0.1, type: "coin" },
  { key: "0.05", label: "5¢", value: 0.05, type: "coin" },
  { key: "0.01", label: "1¢", value: 0.01, type: "coin" },
];

function getDenominations(currencyCode?: string): TCashDenomination[] {
  const code = (currencyCode ?? "cad").toLowerCase();
  return code === "usd" ? USD_DENOMINATIONS : CAD_DENOMINATIONS;
}

function emptyCounts(denominations: TCashDenomination[]): Record<string, string> {
  return denominations.reduce<Record<string, string>>((acc, denomination) => {
    acc[denomination.key] = "";
    return acc;
  }, {});
}

function sumDenominations(
  denominations: TCashDenomination[],
  counts: Record<string, string>,
): number {
  return denominations.reduce((sum, denomination) => {
    const quantity = Number(counts[denomination.key]);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      return sum;
    }
    return sum + quantity * denomination.value;
  }, 0);
}

interface ICashCountSheetProps {
  open: boolean;
  currencyCode?: string;
  onCancel: () => void;
  onApply: (total: number) => void;
}

export default function CashCountSheet(props: ICashCountSheetProps) {
  const { t } = useTranslation();
  const currencyCode = props.currencyCode ?? "cad";
  const denominations = useMemo(() => getDenominations(props.currencyCode), [props.currencyCode]);
  const [counts, setCounts] = useState<Record<string, string>>(() => emptyCounts(denominations));

  const bills = useMemo(() => denominations.filter(d => d.type === "bill"), [denominations]);
  const coins = useMemo(() => denominations.filter(d => d.type === "coin"), [denominations]);

  useEffect(() => {
    if (props.open) {
      setCounts(emptyCounts(denominations));
    }
  }, [props.open, denominations]);

  const billsTotal = useMemo(() => sumDenominations(bills, counts), [bills, counts]);
  const coinsTotal = useMemo(() => sumDenominations(coins, counts), [coins, counts]);
  const total = billsTotal + coinsTotal;

  function handleCountChange(key: string, value: string) {
    if (value !== "" && !/^\d+$/.test(value)) {
      return;
    }
    setCounts(prev => ({ ...prev, [key]: value }));
  }

  function handleClear() {
    setCounts(emptyCounts(denominations));
  }

  function handleApply() {
    props.onApply(Math.round(total * 100) / 100);
  }

  function renderDenominationRow(denomination: TCashDenomination) {
    const quantity = Number(counts[denomination.key] || 0);
    const lineTotal = Number.isFinite(quantity) && quantity > 0 ? quantity * denomination.value : 0;

    return (
      <div key={denomination.key} className="flex items-center gap-2">
        <div style={{ width: "4.5rem", flexShrink: 0 }}>
          <Input
            labelHidden
            value={counts[denomination.key]}
            placeholder="0"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleCountChange(denomination.key, e.target.value)
            }
          />
        </div>
        <span className="text-medium text-gray-500" style={{ flexShrink: 0 }}>
          ×
        </span>
        <span className="text-medium text-gray-900 whitespace-nowrap" style={{ flexShrink: 0 }}>
          {denomination.label}
        </span>
        <span className="text-medium text-gray-500" style={{ flexShrink: 0 }}>
          =
        </span>
        <span className="text-medium text-gray-900 whitespace-nowrap" style={{ minWidth: "4.5rem" }}>
          {displayCurrency(currencyCode, lineTotal)}
        </span>
      </div>
    );
  }

  function renderColumn(title: string, items: TCashDenomination[], columnTotal: number) {
    return (
      <div
        className="flex flex-col gap-2"
        style={{
          border: "1px solid #E5E7EB",
          borderRadius: "8px",
          padding: "12px",
          backgroundColor: "#FAFAFA",
        }}
      >
        <p className="text-bold text-gray-900">{title}</p>
        <div className="flex flex-col gap-2">{items.map(renderDenominationRow)}</div>
        <div
          className="flex items-center justify-between mt-1 pt-2"
          style={{ borderTop: "1px solid #E5E7EB" }}
        >
          <span className="text-medium text-gray-700">{t("Subtotal")}</span>
          <span className="text-medium text-gray-900">{displayCurrency(currencyCode, columnTotal)}</span>
        </div>
      </div>
    );
  }

  return (
    <Portal isMounted={props.open}>
      <Sheet
        open={props.open}
        size="medium"
        title={t("Count Cash")}
        supportText={t("Enter the quantity of each bill and coin. The total will update automatically.")}
        closable
        overflow
        onCancel={props.onCancel}
        cancelText={t("Cancel")}
        okText={t("Apply Total")}
        onOk={handleApply}
        onSecondary={handleClear}
        secondaryText={t("Clear")}
      >
        <div className="flex flex-col gap-3">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "16px",
              alignItems: "start",
            }}
          >
            {renderColumn(t("Bills"), bills, billsTotal)}
            {renderColumn(t("Coins"), coins, coinsTotal)}
          </div>

          <div className="flex flex-col gap-1 mt-1 pt-3" style={{ borderTop: "1px solid #E5E7EB" }}>
            <div className="flex items-center justify-between">
              <p className="text-medium text-gray-700">{t("Bills Total")}</p>
              <p className="text-medium text-gray-900">{displayCurrency(currencyCode, billsTotal)}</p>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-medium text-gray-700">{t("Coins Total")}</p>
              <p className="text-medium text-gray-900">{displayCurrency(currencyCode, coinsTotal)}</p>
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-bold text-gray-900">{t("Total")}</p>
              <p className="text-bold text-gray-900">{displayCurrency(currencyCode, total)}</p>
            </div>
          </div>
        </div>
      </Sheet>
    </Portal>
  );
}
